// @ts-check
// Editor do campo ARRAY (lista de textos) na tabela e no registro, como o ArrayFieldInput do Twenty: um item por linha,
// campo para acrescentar no fim, editar ou remover cada item. Enter acrescenta; com o campo vazio, Enter, Tab, Esc ou
// clique fora gravam a lista.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { addArrayItem, removeArrayItem, replaceArrayItem, toArrayFieldValue, toArrayItems } from '../../lib/array-field.js';
import { LightIconButton } from '../../ui/button.js';
import { useClickOutside } from '../../ui/hooks.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ field: import('../../lib/field-metadata.js').FieldMetadata, record: Record<string, any>, initialCharacter?: string | null,
 *   onCommit: (patch: { apiPatch: Record<string, unknown>, localPatch: Record<string, unknown> }, navigation: 'down' | 'next' | 'previous' | null) => void }} props
 */
export function ArrayFieldInput({ field, record, initialCharacter, onCommit }) {
  const [items, setItems] = useState(() => toArrayItems(record?.[field.name]));
  const [draft, setDraft] = useState(initialCharacter ?? '');
  const committedRef = useRef(false);
  const surfaceRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  /** @param {'down' | 'next' | 'previous' | null} navigation @param {string[]} [finalItems] */
  const commit = (navigation, finalItems) => {
    if (committedRef.current) return;
    committedRef.current = true;
    const value = toArrayFieldValue(finalItems ?? addArrayItem(items, draft), field);
    onCommit({ apiPatch: { [field.name]: value }, localPatch: { [field.name]: value } }, navigation);
  };
  useClickOutside(surfaceRef, () => commit(null));

  return html`<div ref=${surfaceRef} class="field-input-surface array-field-input" data-testid="array-field-input"
    onClick=${(/** @type {Event} */ event) => event.stopPropagation()} onMouseDown=${(/** @type {Event} */ event) => event.stopPropagation()}>
    ${items.map((item, index) => html`<div key=${`${index}-${item}`} class="array-field-item">
      <input class="field-input-control" value=${item} aria-label=${t('recordPage.array.item', { index: index + 1 })}
        onChange=${(/** @type {Event} */ event) => setItems(replaceArrayItem(items, index, /** @type {HTMLInputElement} */ (event.currentTarget).value))}
        onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            commit(null);
          }
        }} />
      <${LightIconButton} icon="IconTrash" accent="tertiary" ariaLabel=${t('recordPage.array.remove', { item })} onClick=${() => setItems(removeArrayItem(items, index))} />
    </div>`)}
    <input
      class="field-input-control"
      value=${draft}
      placeholder=${t('recordPage.array.add')}
      ref=${(/** @type {HTMLInputElement | null} */ element) => {
        if (element && !element.dataset.focused) {
          element.dataset.focused = 'true';
          element.focus();
        }
      }}
      onInput=${(/** @type {Event} */ event) => setDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
      onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
        if (event.key === 'Enter' && draft.trim()) {
          event.preventDefault();
          event.stopPropagation();
          setItems(addArrayItem(items, draft));
          setDraft('');
          return;
        }
        const navigation = event.key === 'Enter' ? 'down' : event.key === 'Tab' ? (event.shiftKey ? 'previous' : 'next') : event.key === 'Escape' ? null : undefined;
        if (navigation === undefined) return;
        event.preventDefault();
        event.stopPropagation();
        commit(navigation);
      }}
    />
  </div>`;
}
